/* reviews-custom.js - fetches Judge.me's widget HTML via the public-safe
   widget endpoint and re-renders the review data into our own markup.
   The /api/v1/reviews JSON endpoint is gated to the private token (which
   we can't expose client-side), so we use /api/v1/widgets/product_review
   which the public token unlocks, and parse out structured data from the
   returned HTML. The summary line + stars come from the existing judgeme
   metafield server-side; this script handles the review list + paging. */
(function () {
  'use strict';

  const section = document.querySelector('[data-reviews-custom]');
  if (!section) return;

  const productId  = section.dataset.productId;
  const shopDomain = section.dataset.shopDomain;
  const apiToken   = section.dataset.jmToken;
  const list       = section.querySelector('[data-reviews-list]');
  const moreBtn    = section.querySelector('[data-reviews-more]');
  const loadingEl  = section.querySelector('[data-reviews-loading]');
  const emptyEl    = section.querySelector('[data-reviews-empty]');
  const errorEl    = section.querySelector('[data-reviews-error]');
  const PER_PAGE   = 10;

  if (!productId || !shopDomain || !apiToken) {
    if (loadingEl) loadingEl.hidden = true;
    if (errorEl)   errorEl.hidden = false;
    return;
  }

  let page = 1;
  let totalReviews = 0;
  let renderedCount = 0;
  let loading = false;

  function escape(str) {
    return String(str || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatDate(input) {
    if (!input) return '';
    // Judge.me ships timestamps like "2026-04-30 18:29:05 UTC" - Safari
    // chokes on the space form, so normalise to ISO before parsing.
    const iso = String(input).replace(' ', 'T').replace(' UTC', 'Z');
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(2);
    return `${dd}/${mm}/${yy}`;
  }

  function starsHtml(rating) {
    const r = Math.max(0, Math.min(5, Math.round(rating || 0)));
    let html = '<span class="rev-card__stars" aria-label="' + r + ' von 5 Sternen">';
    for (let i = 1; i <= 5; i++) {
      html += '<span class="rev-card__star' + (i <= r ? ' is-on' : '') + '">★</span>';
    }
    html += '</span>';
    return html;
  }

  function picturesHtml(pics) {
    if (!pics || !pics.length) return '';
    let html = '<div class="rev-card__pics">';
    pics.slice(0, 4).forEach(function (p) {
      if (!p.src) return;
      html += '<div class="rev-card__pic">' +
              '<img src="' + escape(p.src) + '" alt="" loading="lazy" decoding="async">' +
              '</div>';
    });
    html += '</div>';
    return html;
  }

  function extractBody(bodyEl) {
    if (!bodyEl) return '';
    // Judge.me wraps each paragraph in <p>. textContent would collapse the
    // boundaries, so we read paragraphs and rejoin them with blank lines.
    const ps = bodyEl.querySelectorAll('p');
    if (ps.length) {
      return Array.from(ps).map(function (p) { return (p.textContent || '').trim(); })
        .filter(Boolean).join('\n\n');
    }
    return (bodyEl.textContent || '').trim();
  }

  function parseReviews(widgetHtml) {
    const doc = new DOMParser().parseFromString(widgetHtml, 'text/html');

    // Wrapper carries summary data (we use it to know when to hide "more").
    const wrap = doc.querySelector('.jdgm-rev-widg');
    if (wrap) {
      const n = parseInt(wrap.getAttribute('data-number-of-reviews') || '0', 10);
      if (!isNaN(n)) totalReviews = n;
    }

    const items = [];
    doc.querySelectorAll('.jdgm-rev').forEach(function (el) {
      const ratingEl = el.querySelector('.jdgm-rev__rating');
      const timeEl   = el.querySelector('.jdgm-rev__timestamp');
      const authorEl = el.querySelector('.jdgm-rev__author');
      const titleEl  = el.querySelector('.jdgm-rev__title');
      const bodyEl   = el.querySelector('.jdgm-rev__body');
      // Judge.me wraps each picture in <a><img></a>. Match anchors first;
      // fall back to bare <img> only if no anchors are present (keeps every
      // picture from rendering twice).
      const pics = [];
      const anchors = el.querySelectorAll('.jdgm-rev__pics a');
      if (anchors.length) {
        anchors.forEach(function (a) {
          const img = a.querySelector('img');
          const src = img && (img.getAttribute('src') || img.getAttribute('data-src'));
          if (src) pics.push({ src: src });
        });
      } else {
        el.querySelectorAll('.jdgm-rev__pics img').forEach(function (img) {
          const src = img.getAttribute('src') || img.getAttribute('data-src');
          if (src) pics.push({ src: src });
        });
      }

      // Shop reply: Judge.me nests it inside each .jdgm-rev as
      // .jdgm-rev__reply > .jdgm-rev__reply-content (empty div when no reply).
      const replyEl     = el.querySelector('.jdgm-rev__reply-content');
      const replierEl   = el.querySelector('.jdgm-rev__replier');

      items.push({
        id:       el.getAttribute('data-review-id') || '',
        verified: el.getAttribute('data-verified-buyer') === 'true',
        rating:   ratingEl ? parseInt(ratingEl.getAttribute('data-score') || '0', 10) : 0,
        date:     timeEl   ? timeEl.getAttribute('data-content') || ''                : '',
        author:   authorEl ? (authorEl.textContent || '').trim()                       : '',
        title:    titleEl  ? (titleEl.textContent  || '').trim()                       : '',
        body:     extractBody(bodyEl),
        pics:     pics,
        reply:    replyEl   ? extractBody(replyEl)                    : '',
        replier:  replierEl ? (replierEl.textContent || '').trim()   : ''
      });
    });
    return items;
  }

  function renderReview(r) {
    const name = r.author || 'Anonym';
    return '' +
      '<article class="rev-card">' +
        '<header class="rev-card__head">' +
          starsHtml(r.rating) +
          '<time class="rev-card__date t-mono" datetime="' + escape(r.date) + '">' +
            escape(formatDate(r.date)) +
          '</time>' +
        '</header>' +
        '<div class="rev-card__author">' +
          '<span class="rev-card__name">' + escape(name) + '</span>' +
          (r.verified
            ? '<span class="rev-card__verified t-mono">Verifiziert</span>'
            : '') +
        '</div>' +
        (r.title ? '<h3 class="rev-card__title">' + escape(r.title) + '</h3>' : '') +
        (r.body  ? '<div class="rev-card__body">' + escape(r.body).replace(/\n/g, '<br>') + '</div>' : '') +
        picturesHtml(r.pics) +
        (r.reply
          ? '<div class="rev-card__reply">' +
              '<span class="rev-card__reply-author t-mono">Antwort von ' + escape(r.replier || 'BONIFAKTUR') + '</span>' +
              '<div class="rev-card__reply-body">' + escape(r.reply).replace(/\n/g, '<br>') + '</div>' +
            '</div>'
          : '') +
      '</article>';
  }

  function fetchPage(p) {
    if (loading) return;
    loading = true;
    if (loadingEl) loadingEl.hidden = false;
    if (errorEl)   errorEl.hidden   = true;

    const url = 'https://judge.me/api/v1/widgets/product_review' +
      '?api_token='   + encodeURIComponent(apiToken) +
      '&shop_domain=' + encodeURIComponent(shopDomain) +
      '&external_id=' + encodeURIComponent(productId) +
      '&per_page='    + PER_PAGE +
      '&page='        + p;

    fetch(url, { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(function (data) {
        const reviews = parseReviews(data && data.widget || '');

        if (loadingEl) loadingEl.hidden = true;

        if (p === 1 && reviews.length === 0) {
          if (emptyEl) emptyEl.hidden = false;
          if (moreBtn) moreBtn.hidden = true;
          return;
        }

        list.insertAdjacentHTML('beforeend', reviews.map(renderReview).join(''));
        renderedCount += reviews.length;

        if (moreBtn) moreBtn.hidden = (renderedCount >= totalReviews);
        page = p + 1;
      })
      .catch(function () {
        if (loadingEl) loadingEl.hidden = true;
        if (errorEl)   errorEl.hidden   = false;
      })
      .finally(function () { loading = false; });
  }

  fetchPage(1);

  if (moreBtn) {
    moreBtn.addEventListener('click', function () { fetchPage(page); });
  }
})();
